import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, {
  type SharedValue,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import { BlurView } from 'expo-blur';
import { BodyText, Icon, theme } from '@sg/garnish';

import { useProductDetailsNavigationHeaderHeight } from './useProductDetailsNavigationHeaderHeight';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsNavigationHeader = (props: Props) => {
  const {
    title,
    foregroundColor = theme.colors.WHITE,
    backgroundColor = theme.colors.KALE,
    scrollOffsetSV,
    closeButtonA11yLabel,
    onCloseButtonPress,
  } = props;

  const navHeaderHeight = useProductDetailsNavigationHeaderHeight();

  // ─────────────────────────────────────────────────────────────────────

  const backdropOpacitySV = useDerivedValue(() => {
    return withTiming(scrollOffsetSV.value > 0 ? NAVIGATION_HEADER_OPACITY : 0);
  }, [scrollOffsetSV]);

  const backdropBlurOpacitySV = useDerivedValue(() => {
    return withTiming(scrollOffsetSV.value > 0 ? 1 : 0);
  }, [scrollOffsetSV]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(
    () => ({ height: navHeaderHeight }),
    [navHeaderHeight],
  );

  const backdropBlurDynamicStyles = useStyle(
    () => ({ height: navHeaderHeight }),
    [navHeaderHeight],
  );

  const backdropDynamicStyles = useStyle(
    () => ({ backgroundColor }),
    [backgroundColor],
  );

  const backdropAnimatedStyles = useAnimatedStyle(() => ({
    opacity: backdropOpacitySV.value,
  }));

  const backdropBlurAnimatedStyles = useAnimatedStyle(() => ({
    opacity: backdropBlurOpacitySV.value,
  }));

  const titleDynamicStyles = useStyle(
    () => ({ color: foregroundColor }),
    [foregroundColor],
  );

  const closeButtonBackdropDynamicStyles = useStyle(
    () => ({ backgroundColor: foregroundColor }),
    [foregroundColor],
  );

  const containerStyles = [styles.container, containerDynamicStyles];
  const backdropStyles = [styles.backdrop, backdropDynamicStyles, backdropAnimatedStyles]; // prettier-ignore
  const backdropBlurStyles = [styles.backdropBlur, backdropBlurDynamicStyles, backdropBlurAnimatedStyles]; // prettier-ignore
  const closeButtonBackdropStyles = [styles.closeButtonBackdrop, closeButtonBackdropDynamicStyles]; // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  return (
    <SafeAreaView edges={SAFE_AREA_EDGES} style={containerStyles}>
      {/* 
          NOTE: On web, there are some visual artifacts when the backdrop is
                semi-transparent and there is a blur effect. So we ignore the
                blur effect on web.
      */}
      {Platform.OS === 'web' ? null : (
        <Animated.View style={backdropBlurStyles}>
          <BlurView
            intensity={48}
            style={StyleSheet.absoluteFill}
            experimentalBlurMethod={
              Platform.OS === 'android' ? 'dimezisBlurView' : 'none'
            }
          />
        </Animated.View>
      )}

      <Animated.View style={backdropStyles} />

      <SafeAreaView edges={SAFE_AREA_EDGES} style={styles.titleContainer}>
        <BodyText sizeMatch={['16']} style={titleDynamicStyles}>
          {title}
        </BodyText>
      </SafeAreaView>

      <TouchableOpacity
        accessibilityLabel={closeButtonA11yLabel}
        accessibilityRole="button"
        onPress={onCloseButtonPress}
        style={styles.closeButton}
      >
        <View style={closeButtonBackdropStyles} />

        <Icon name="IconClose" color={foregroundColor} />
      </TouchableOpacity>
    </SafeAreaView>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['top'],
  default: [],
});

const CLOSE_BUTTON_SIZE = 40;

// NOTE: On web, there are some visual artifacts when the backdrop is
//       semi-transparent and there is a blur effect. So we ignore the blur
//       effect there.
const NAVIGATION_HEADER_OPACITY = Platform.select({ web: 1, default: 0.8 });

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['4'],
  },
  backdropBlur: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  titleContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['18'],
  },
  closeButton: {
    width: CLOSE_BUTTON_SIZE,
    height: CLOSE_BUTTON_SIZE,
    borderRadius: CLOSE_BUTTON_SIZE,
    alignSelf: 'flex-end',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonBackdrop: {
    ...StyleSheet.absoluteFillObject,
    width: CLOSE_BUTTON_SIZE,
    height: CLOSE_BUTTON_SIZE,
    borderRadius: CLOSE_BUTTON_SIZE,
    opacity: 0.2,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  title: string;
  foregroundColor: string | undefined;
  backgroundColor: string | undefined;
  scrollOffsetSV: SharedValue<number>;
  closeButtonA11yLabel: string;
  onCloseButtonPress: () => void;
};
